import type { OrgBillingStatus, OrgCreditsResponse } from '@/services/org-admin';

/**
 * What the Billing screen is waiting for after a Stripe operation, and how it knows the wait is over.
 *
 * THE PROBLEM THIS FIXES. Stripe is asynchronous: a purchase returns "processing", and the plan or
 * budget only moves when the webhook reaches our backend. So the screen polls. It did that by
 * refetching BOTH `/billing` and `/billing/credits` every 3 seconds for a full minute, unconditionally
 * — about 40 requests per purchase — and it kept going for the remaining 50-odd seconds after the
 * webhook had already landed, because nothing looked at the responses to notice.
 *
 * Two things were wrong with that, and this module is the fix for both:
 *
 *   1. **It never stopped early.** The poll exists to observe one change. Once the change is visible
 *      there is nothing left to wait for, so the window can close. Typical case: one or two polls
 *      instead of twenty.
 *
 *   2. **It polled the wrong things.** A plan purchase cannot change the org credit balance, and a
 *      seat top-up changes neither the plan nor the balance — it moves that seat's BUDGET, which
 *      lives in `/usage` and was not being polled at all. So a top-up used to poll two payloads that
 *      could not answer it and none that could, then give up after a minute. `SETTLING_TARGETS` maps
 *      each operation to the payload that actually carries its outcome.
 *
 * Signatures project only the awaited fields rather than hashing the whole payload. That matters most
 * for usage: spend figures tick constantly, so a whole-payload comparison would report a top-up as
 * "landed" the moment anyone spent a cent, which is the opposite of what is being waited for.
 */

/** The payloads a pending Stripe operation can land in. */
export type SettlingTarget = 'billing' | 'credits' | 'usage';

/** Why the screen is polling — i.e. what the admin just did. */
export type SettlingReason = 'plan' | 'credit' | 'budget';

/**
 * Which payload each operation's outcome appears in.
 *
 * `credit` is the org-level credit purchase: it lands in the balance. `budget` covers a seat top-up,
 * single or bulk, whose money arrives as that seat's `budget_usd` in the usage rollup. `plan` is a
 * subscription purchase or tier change, visible on the billing status.
 *
 * Credit ASSIGNMENT is deliberately absent: it takes no money and involves no webhook, so the budgets
 * have already moved by the time the mutation resolves and there is nothing to wait for.
 */
export const SETTLING_TARGETS: Record<SettlingReason, readonly SettlingTarget[]> = {
  plan: ['billing'],
  credit: ['credits'],
  budget: ['usage'],
};

/** A comparable fingerprint per target. `null` while that payload has not arrived. */
export type SettlingSnapshot = Partial<Record<SettlingTarget, string | null>>;

/**
 * The plan-side facts a purchase or tier change moves.
 *
 * Sorted by user id so a backend that reorders `members` between responses does not read as a change.
 */
export function billingSignature(status: OrgBillingStatus | undefined): string | null {
  if (!status) return null;
  const seats = [...status.members]
    .sort((a, b) => a.user_id.localeCompare(b.user_id))
    .map((row) =>
      [
        row.user_id,
        row.plan ?? '',
        row.billing_mode ?? '',
        row.subscription_status ?? '',
        row.plan_cycle_ends_at ?? '',
      ].join(':'),
    );
  // The card is included because a first purchase through Checkout also puts one on file, and that
  // arrival is part of what the admin is waiting to see.
  const card = status.payment_method
    ? `${status.payment_method.brand}:${status.payment_method.last4}:${status.payment_method.exp_month}/${status.payment_method.exp_year}`
    : String(status.has_payment_method);
  return `${card}|${seats.join(',')}`;
}

/** The only credit fact a purchase moves. Entries are ignored: the balance is the outcome. */
export function creditsSignature(credits: OrgCreditsResponse | undefined): string | null {
  if (!credits) return null;
  return String(credits.balance_usd);
}

/**
 * Per-seat budgets, and deliberately NOT spend.
 *
 * A top-up raises `budget_usd`. `spend_usd` moves whenever anybody runs anything, so including it
 * would end the wait on unrelated activity and report someone else's usage as this admin's purchase
 * arriving.
 */
/*
 * Structurally typed rather than tied to `OrgUsageRollup`, because Billing now reads the
 * month-scoped report (the same payload Usage and Overview use) instead of the `/usage`
 * rollup. Both carry `user_id` and `budget_usd`, and a top-up still lands as `budget_usd`,
 * so the settling watch is unchanged — only the payload it arrives on.
 */
export function usageSignature(
  usage: { members: Array<{ user_id: string; budget_usd: number }> } | undefined,
): string | null {
  if (!usage) return null;
  return [...usage.members]
    .sort((a, b) => a.user_id.localeCompare(b.user_id))
    .map((row) => `${row.user_id}:${row.budget_usd}`)
    .join(',');
}

/**
 * Advance the wait: has the awaited change landed, and what is the baseline now?
 *
 * WHY THIS IS NOT JUST "DID THE SIGNATURE CHANGE". The wait often begins before the payload it is
 * watching has loaded at all — on the return from Stripe Checkout the component mounts with
 * `?checkout=success` already in the URL, so the window opens in the same commit that fires the
 * first `/billing` request. The baseline is therefore `null`, and treating the first response as
 * "changed" closed the window instantly, before a single poll. That is exactly the bug this
 * function's first branch exists to prevent, and it is worse than the over-polling it replaced: the
 * screen stopped looking at all.
 *
 * So a `null → value` transition ADOPTS the value as the baseline instead of settling. The wait
 * genuinely starts from the first real data, and only a change away from that ends it. A webhook
 * that had already landed before the first load is covered by the timeout backstop, which is what
 * covered it before any of this existed.
 */
export function reconcileSettling(
  baseline: SettlingSnapshot,
  current: SettlingSnapshot,
  targets: readonly SettlingTarget[],
): { settled: boolean; baseline: SettlingSnapshot } {
  let settled = false;
  const next: SettlingSnapshot = { ...baseline };

  for (const target of targets) {
    const before = baseline[target] ?? null;
    const now = current[target] ?? null;
    // Not loaded yet — nothing to compare, and nothing to conclude.
    if (now === null) continue;
    if (before === null) {
      // First real data. This is where the wait actually begins.
      next[target] = now;
      continue;
    }
    if (before !== now) settled = true;
  }

  return { settled, baseline: next };
}
